import {
  $el,
  $list,
  isNotThemeStore,
  whenDefined,
} from 'src/scripts/utils/utils'
import { BaseComponent } from 'src/scripts/components/base-component'
import {
  CarouselComponent,
  CarouselDots,
} from 'src/scripts/components/carousel'
import { debounce } from 'src/scripts/utils/debounce'
import { isEnterKey } from 'src/scripts/utils/key'

export class ImageSlider extends BaseComponent {
  withProgressBar: boolean
  withRotateImages: boolean
  withVerticalScrollText: boolean
  withVerticalScrollImage: boolean

  carousel: CarouselComponent
  progressBar: HTMLElement
  progressBarIndicator: HTMLElement
  progressDots: HTMLElement[]
  images: HTMLElement[]
  captions: HTMLElement[]
  arrows: HTMLElement[]
  dots: CarouselDots['dots']
  length: number
  imagesLength: number
  captionsLength: number
  activeIndex: number
  imagesContainer: HTMLElement
  captionHeight: number

  touchStartX: number

  intersectionObserver: IntersectionObserver
  resizeObserver: ResizeObserver
  isMobile: boolean

  windowClientHeight: number
  scrollTriggerTop: number
  scrollTriggerBottom: number
  scrollDirection: string
  lastScroll: number

  constructor() {
    super()

    this.withProgressBar = this.hasAttribute('with-progress-bar')
    this.withRotateImages = this.hasAttribute('with-rotate-images')
    this.withVerticalScrollText = this.hasAttribute('with-vertical-scroll')
    this.withVerticalScrollImage = this.hasAttribute(
      'with-vertical-scroll-image',
    )

    this.carousel = $el('carousel-component', this)

    if (this.withRotateImages) {
      this.images = $list('[data-image-slider-image]', this)
      this.captions = $list('[data-image-slider-caption]', this)
      this.arrows = $list('[data-direction]', this)
      this.dots = $list('[data-progress-dot]')
      this.length = this.images.length - 1
      this.activeIndex = 0
    }

    if (this.withProgressBar) {
      this.progressBar = $el('carousel-progress', this)

      this.progressBarIndicator = this.querySelector(
        '[data-carousel-progress-indicator]',
      )
      this.progressDots = $list('[data-progress-dot]', this)
    }

    if (this.withVerticalScrollText || this.withVerticalScrollImage) {
      this.imagesContainer = this.querySelector('[data-carousel-viewport]')
      this.images = $list('[data-image-slider-image]', this)
      this.captions = $list('[data-image-slider-caption]', this)

      this.captionHeight = this.images[0].offsetHeight
      this.imagesLength = this.images.filter(
        img => img.firstElementChild.tagName !== 'svg',
      ).length
      this.captionsLength = this.captions.length
      this.activeIndex = 0
    }
  }

  mountComponent(): void {
    if (this.withProgressBar && !this.withRotateImages) {
      whenDefined('carousel-component').then(() => {
        this.carousel?.on('carouselSelect', this.handleCarouselSelect)
      })
    }

    if (this.withRotateImages) {
      if (!this.withVerticalScrollText) {
        this.stackImages()
      }

      this.arrows?.forEach(arrow => {
        arrow.addEventListener('click', this.handleArrowClick)
        arrow.addEventListener('keydown', this.handleArrowKeydown)
      })

      if (this.withProgressBar) {
        this.dots?.forEach(dot => {
          dot.addEventListener('click', this.handleDotClick)
        })
      }

      this.rotateImages()
      this.toggleArrowsDisabled()
    }

    if (this.withRotateImages && !this.withVerticalScrollText) {
      this.setAttribute('draggable', 'true')

      this.addEventListener('dragstart', this.handleDragStart)
      this.addEventListener('mousedown', this.handleTouchStart)
      this.addEventListener('touchstart', this.handleTouchStart)

      this.addEventListener('mouseup', this.handleTouchEnd)
      this.addEventListener('touchend', this.handleTouchEnd)
    }

    if (this.withVerticalScrollText) {
      this.stackImagesForVerticalScroll()
      this.setObserversForVerticalScroll()
    }

    if (this.withVerticalScrollImage) {
      this.setObserversForVerticalScrollImage()
      this.addListener(this, 'focusout', this.handleVerticalImageFocusout)
    }

    this.setComponentVisible()
  }

  unmountComponent(): void {
    if (this.withProgressBar && !this.withRotateImages) {
      this.carousel?.off('carouselSelect', this.handleCarouselSelect)
    }

    if (this.withRotateImages) {
      this.arrows?.forEach(arrow => {
        arrow.removeEventListener('click', this.handleArrowClick)
        arrow.removeEventListener('keydown', this.handleArrowKeydown)
      })

      if (this.withProgressBar) {
        this.dots?.forEach(dot => {
          dot.removeEventListener('click', this.handleDotClick)
        })
      }
    }

    if (this.withRotateImages && !this.withVerticalScrollText) {
      this.removeEventListener('dragstart', this.handleTouchStart)
      this.removeEventListener('mousedown', this.handleTouchStart)
      this.removeEventListener('touchstart', this.handleTouchStart)

      this.removeEventListener('mouseup', this.handleTouchEnd)
      this.removeEventListener('touchend', this.handleTouchEnd)
    }

    if (this.withVerticalScrollText) {
      this.intersectionObserver.disconnect()
      this.resizeObserver.disconnect()
    }

    window.removeEventListener('scroll', this.handleChangeOpacity)
  }

  handleVerticalImageFocusout = event => {
    const focusTarget = event.relatedTarget as HTMLElement

    if (this.contains(focusTarget)) {
      const block = focusTarget.closest('[data-image-slider-caption]')
      const blockId = block.getAttribute('data-image-slider-caption')
      const imageSlider = $el(`[data-image-slider-image="${blockId}"]`)

      if (imageSlider && isNotThemeStore()) {
        imageSlider.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  handleVerticalScroll = (entries: IntersectionObserverEntry[]): void => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const inViewIndex = Number(
          entry.target.parentElement.getAttribute('data-image-slider-caption'),
        )

        if (inViewIndex === this.activeIndex) {
          return
        }

        if (this.imagesLength === 1) {
          this.activeIndex = inViewIndex
          return
        }

        if (this.withRotateImages) {
          const direction = inViewIndex > this.activeIndex ? 'next' : 'prev'
          this.changeSlides(direction, inViewIndex)
        } else {
          this.images.forEach((img, index) => {
            if (index === inViewIndex) {
              img.setAttribute('visible', '')
              img.removeAttribute('prev')
              img.removeAttribute('next')
            } else if (index < inViewIndex) {
              img.setAttribute('prev', '')
              img.removeAttribute('visible')
              img.removeAttribute('next')
            } else if (index > inViewIndex) {
              img.setAttribute('next', '')
              img.removeAttribute('visible')
              img.removeAttribute('prev')
            }
          })
        }

        this.activeIndex = inViewIndex
      }
    })
  }

  setObserversForVerticalScroll = (): void => {
    let imagesHeight = this.imagesContainer.getBoundingClientRect().height
    this.windowClientHeight = document.documentElement.clientHeight

    if (imagesHeight > this.windowClientHeight) {
      this.images.forEach(image => {
        image.style.maxHeight = `${this.windowClientHeight - 30}px`
      })

      imagesHeight = this.imagesContainer.getBoundingClientRect().height
    }

    const topToSet = (this.windowClientHeight - imagesHeight) / 2

    this.imagesContainer.style.top = `${topToSet}px`

    this.scrollTriggerTop = topToSet
    this.scrollTriggerBottom =
      this.windowClientHeight - this.scrollTriggerTop - imagesHeight

    this.intersectionObserver = new IntersectionObserver(
      this.handleVerticalScroll,
      { rootMargin: `0px 0px -${this.scrollTriggerBottom + 30}px 0px` },
    )

    this.captions.forEach(caption => {
      caption.style.minHeight = `${this.captionHeight}px`

      if (caption.firstElementChild) {
        this.intersectionObserver.observe(caption.firstElementChild)
      }
    })

    if (this.imagesLength === 1) {
      this.images.forEach((img, index) => {
        if (img.firstElementChild.tagName !== 'svg') {
          img.setAttribute('visible', '')
          img.removeAttribute('prev')
          img.removeAttribute('next')
        } else {
          img.removeAttribute('next')
          img.removeAttribute('visible')
          img.removeAttribute('prev')
          img.style.opacity = '0'
        }
      })
    }

    this.resizeObserver = new ResizeObserver(this.handleResizeVerticalScroll)
    this.resizeObserver.observe(this)

    this.setObserveScroll()
  }

  setObserveScroll = (): void => {
    this.lastScroll = 0
    window.addEventListener('scroll', this.handleChangeOpacity)
  }

  handleChangeOpacity = () => {
    const scrollTop = document.documentElement.scrollTop
    const scrollDirection = this.lastScroll > scrollTop ? 'up' : 'down'
    this.lastScroll = scrollTop

    this.captions.forEach((caption, index) => {
      const bottom = caption.lastElementChild.getBoundingClientRect().bottom
      const top = caption.firstElementChild.getBoundingClientRect().top

      if (
        bottom < 0 ||
        top > this.windowClientHeight ||
        (index === this.captions.length - 1 &&
          this.windowClientHeight - bottom > this.scrollTriggerBottom &&
          scrollDirection === 'down')
      ) {
        caption.setAttribute('dont-set-opacity', '')
        caption.style.opacity = `1`
        return
      }

      const captionHeight = bottom - top

      const persentToTop = (bottom - this.scrollTriggerTop) / captionHeight
      const persentToBottom =
        (this.windowClientHeight - top - this.scrollTriggerBottom) /
        captionHeight

      if (!caption.hasAttribute('dont-set-opacity')) {
        caption.style.opacity =
          top < this.scrollTriggerTop ? `${persentToTop}` : `${persentToBottom}`
      }

      const bottomLimit =
        caption.getBoundingClientRect().height <
        this.imagesContainer.getBoundingClientRect().height
          ? this.scrollTriggerBottom
          : this.scrollTriggerBottom -
            (caption.getBoundingClientRect().height -
              this.imagesContainer.getBoundingClientRect().height +
              20)

      if (
        caption.hasAttribute('dont-set-opacity') &&
        this.windowClientHeight - bottom > bottomLimit &&
        top > this.scrollTriggerTop
      ) {
        caption.removeAttribute('dont-set-opacity')
      }
    })
  }

  handleVerticalScrollImage = (entries: IntersectionObserverEntry[]): void => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const inViewIndex = Number(
          entry.target.getAttribute('data-image-slider-image'),
        )

        if (inViewIndex === this.activeIndex) {
          return
        }

        this.images[inViewIndex].setAttribute('visible', '')
        this.images[this.activeIndex].removeAttribute('visible')

        if (this.captionsLength > 1) {
          this.captions[inViewIndex].setAttribute('visible', '')
          this.captions[this.activeIndex].removeAttribute('visible')
        }

        this.activeIndex = inViewIndex
      }
    })
  }

  setObserversForVerticalScrollImage = (): void => {
    const windowHeight = document.documentElement.clientHeight

    this.intersectionObserver = new IntersectionObserver(
      this.handleVerticalScrollImage,
      { rootMargin: `-40% 0% -50% 0%` },
    )

    let captionMaxHeight = 0

    this.captions.forEach((caption, index) => {
      const top = caption.firstElementChild.getBoundingClientRect().top
      const bottom = caption.lastElementChild.getBoundingClientRect().bottom

      const height = bottom - top

      if (captionMaxHeight < height) {
        captionMaxHeight = height
      }

      if (height < this.images[index].getBoundingClientRect().height) {
        caption.style.height =
          this.images[index].getBoundingClientRect().height + 'px'
      }
    })

    const captionIsHigherThenImages = this.images.find(
      image => image.getBoundingClientRect().height < captionMaxHeight,
    )

    if (captionIsHigherThenImages) {
      this.imagesContainer.style.gap =
        (captionMaxHeight - this.images[0].getBoundingClientRect().height) * 2 +
        'px'
    }

    const captionsParent = this.captions[0].parentElement

    if (captionMaxHeight < windowHeight) {
      const parentHeight = captionsParent.getBoundingClientRect().height
      const topToSet = (windowHeight - parentHeight) / 2
      captionsParent.style.top = `${topToSet}px`
    } else {
      captionsParent.style.top = '0px'
    }

    this.images.forEach(image => {
      this.intersectionObserver.observe(image)
    })

    if (this.captionsLength === 1) {
      this.captions[0].setAttribute('visible', '')
    }
  }

  handleArrowClick = (event): void => {
    const direction = event.currentTarget.getAttribute('data-direction')
    this.arrowMove(direction)
  }

  handleArrowKeydown = (event): void => {
    const direction = event.currentTarget.getAttribute('data-direction')
    if (isEnterKey(event)) {
      this.arrowMove(direction)
    }
  }

  arrowMove(direction: string) {
    let newIndex: number

    if (direction === 'next') {
      newIndex = this.activeIndex === this.length ? 0 : this.activeIndex + 1
    } else {
      newIndex = this.activeIndex - 1 < 0 ? this.length : this.activeIndex - 1
    }

    this.changeSlides(direction, newIndex)

    if (this.withRotateImages) {
      this.toggleArrowsDisabled()
    }

    if (this.withProgressBar && this.withRotateImages) {
      this.toogleProgressDotActive()
      this.setProgressIndicator()
    }
  }

  toggleArrowsDisabled = () => {
    this.arrows[0].toggleAttribute('disabled', this.activeIndex === 0)
    this.arrows[1].toggleAttribute('disabled', this.activeIndex === this.length)
  }

  handleDotClick = (event): void => {
    const index = event.currentTarget.hasAttribute('data-dot-index')
      ? +event.currentTarget.getAttribute('data-dot-index')
      : +event.target
          .closest('[data-progress-dot]')
          .getAttribute('data-dot-index')

    if (index === this.activeIndex) {
      return
    }

    let direction = index > this.activeIndex ? 'next' : 'prev'

    if (this.activeIndex === 0 && index === this.length) {
      direction = 'prev'
    }
    if (index - this.activeIndex > 1) {
      direction = 'prev'
    }
    if (this.activeIndex === this.length && index === 0) {
      direction = 'next'
    }

    this.changeSlides(direction, index)

    if (this.withProgressBar && this.withRotateImages) {
      this.toogleProgressDotActive()
      this.setProgressIndicator()
      this.toggleArrowsDisabled()
    }
  }

  changeSlides = (direction: string, newIndex: number): void => {
    const currentCaption = this.captions[this.activeIndex]
    currentCaption.addEventListener('transitionend', this.animationCaptionEnd)
    currentCaption.removeAttribute('visible')

    const imageToAnimate =
      direction === 'next'
        ? this.images[this.activeIndex]
        : this.images[newIndex]

    imageToAnimate.addEventListener('animationend', this.animationImageEnd)

    if (direction === 'next') {
      imageToAnimate.setAttribute('animating-forward', '')
    } else {
      imageToAnimate.setAttribute('animating-backward', '')
    }

    this.activeIndex = newIndex
  }

  animationCaptionEnd = (event): void => {
    const element = event.currentTarget

    this.captions[this.activeIndex].setAttribute('visible', '')

    element.removeEventListener('transitionend', this.animationCaptionEnd)
  }

  animationImageEnd = (event): void => {
    const element = event.currentTarget

    element.removeAttribute('animating-forward')
    element.removeAttribute('animating-backward')
    element.removeEventListener('animationend', this.animationImageEnd)

    this.stackImages()
  }

  stackImages = (): void => {
    this.images.forEach((img, index) => {
      if (index === this.activeIndex) {
        img.style.zIndex = String(0)
      } else if (index > this.activeIndex) {
        img.style.zIndex = String(this.activeIndex - index)
      } else if (index < this.activeIndex) {
        img.style.zIndex = String(-this.length - index + 1)
      }
    })
  }

  stackImagesForVerticalScroll = (): void => {
    this.images.forEach((img, index) => {
      img.style.zIndex = this.withRotateImages ? `-${index}` : `${index}`
    })
  }

  handleResizeVerticalScroll = debounce((): void => {
    this.captionHeight = this.images[0].offsetHeight

    this.captions.forEach(caption => {
      caption.style.minHeight = `${this.captionHeight}px`
    })
  }, 300)

  rotateImages = (): void => {
    if (this.withVerticalScrollImage) {
      this.images.forEach((img, index) => {
        img.style.transform =
          index % 2 === 0 ? `rotate(${1.7}deg)` : `rotate(-${1.7}deg)`
      })
    } else {
      let prevMinus = 0
      let prevPlus = 0

      this.images.forEach((img, index) => {
        if ((index + 1) % 3 === 0) {
          const deg = prevMinus + 2.5

          img.style.transform = `rotate(${deg}deg)`
          prevMinus = deg
        } else {
          const deg = prevPlus - 1.5

          img.style.transform = `rotate(${deg}deg)`
          prevPlus = deg
        }
      })
    }
  }

  handleCarouselSelect = (): void => {
    this.toogleProgressDotActive()
  }

  toogleProgressDotActive = (): void => {
    const selectedScrollSnap = this.getSelectedScrollSnap()

    this.progressDots?.forEach((dot, index) => {
      dot.classList.toggle(
        'image-slider__progress-bar-dot--passed',
        index < selectedScrollSnap,
      )
      dot.classList.toggle('is-primary', index === this.activeIndex)
    })
  }

  setProgressIndicator = (): void => {
    const selectedScrollSnap = this.getSelectedScrollSnap()
    const progress = (selectedScrollSnap / this.length) * 100

    this.progressBarIndicator.style.transform = `translate3d(${progress}%, 0, 0)`
  }

  getSelectedScrollSnap = (): number => {
    return this.carousel?.embla.selectedScrollSnap() ?? this.activeIndex
  }

  setComponentVisible = (): void => {
    this.setAttribute('visible', '')
  }

  handleDragStart = event => {
    event.preventDefault()
    return false
  }

  handleTouchStart = event => {
    this.touchStartX = event.clientX ?? event.touches[0].clientX
  }

  handleTouchEnd = event => {
    if (event.target.closest('[data-progress-dot]')) {
      this.handleDotClick(event)
      return
    }

    if (this.touchStartX === 0) {
      return
    }

    const currentX = event.clientX ?? event.changedTouches[0].clientX

    const swipeLeft = currentX - this.touchStartX < -20
    const swipeRight = currentX - this.touchStartX > 20

    if (swipeLeft && this.activeIndex !== this.length) {
      this.handleSwipe('next')
    }

    if (swipeRight && this.activeIndex !== 0) {
      this.handleSwipe('prev')
    }

    this.touchStartX = 0
  }

  handleSwipe = (direction: string): void => {
    let newIndex: number

    if (direction === 'next') {
      newIndex = this.activeIndex === this.length ? 0 : this.activeIndex + 1
    } else {
      newIndex = this.activeIndex - 1 < 0 ? this.length : this.activeIndex - 1
    }

    this.changeSlides(direction, newIndex)

    if (this.withRotateImages) {
      this.toggleArrowsDisabled()
    }

    if (this.withProgressBar && this.withRotateImages) {
      this.toogleProgressDotActive()
      this.setProgressIndicator()
    }
  }
}
